import {getMapOrSetArray} from "./maps";
import {rngIntMax} from "./math";

export function isUndefined<T>(x: T, i?: number, array?: Array<T> | ReadonlyArray<T>) {
    return x === undefined;
}

export function isDefined<T>(x: T, i?: number, array?: Array<T> | ReadonlyArray<T>) {
    return !isUndefined(x, i, array);
}

export function mapValuesToArray<T>(map: Map<any, T>): Array<T> {
    let entities: Array<T> = [];
    map.forEach(it => {
        entities.push(it);
    });
    return entities;
}

export function range(start: number, end: number): Array<number> {
    return Array.apply(0, Array(end - start))
        .map((it: any, i: number) => {
            return i + start;
        });
}

export function fastSplice<T>(array: Array<T>, startIndex: number, removeCount: number): T | undefined {
    let len = array.length;
    let removeLen;
    let x;

    if (startIndex >= len || removeCount === 0) {
        return;
    }

    removeCount = startIndex + removeCount > len ? len - startIndex : removeCount;
    removeLen = len - removeCount;
    x = array[startIndex];

    for (let i = startIndex; i < len; i += 1) {
        array[i] = array[i + removeCount];
    }

    array.length = removeLen;

    return x;
}

export function copyIntoArray<T>(destination: Array<T>, source: Array<T>) {
    destination.push.apply(destination, source);
}

export function flatMap<T, U>(arr: Array<T | T[]>,
                              callbackfn: (value: T, index: number, array: T[]) => U): Array<U> {
    let results: Array<U> = [];

    for (let i = 0; i < arr.length; i++) {
        let elem = arr[i];
        if (elem instanceof Array) {
            flatMap(elem, callbackfn)
                .forEach(it => results.push(it));
        }
        else {
            results.push(callbackfn(elem, i, arr as any));
        }
    }

    return results;
}

export function zip<T, U>(array1: Array<T>, array2: Array<U>): Array<[T, U | Array<U> | undefined]> {
    let out: Array<[T, U | Array<U> | undefined]> = [];
    for (let i = 0; i < array1.length - 1; i++) {
        let em2 = (array2.length > i) ? array2[i] : undefined;
        out.push([array1[i], em2]);
    }

    let i = array1.length - 1;
    let em2 = (array1.length === array2.length) ? array2[i] : (array2.length > i ? array2.slice(i) : undefined);
    out.push([array1[i], em2]);

    return out;
}

export function zipSame<T, U>(array1: Array<T>, array2: Array<U>): Array<[T, U]> {
    return zip(array1, array2) as Array<[T, U]>;
}

export function sortDesc(a: number, b: number): number {
    return b - a;
}

export function sortAsc(a: number, b: number): number {
    return a - b;
}

export function uniq<T extends string | number>(array: Array<T>): Array<T> {
    let set = new Set<T>();
    let res: T[] = [];
    for (const it of array) {
        if (set.has(it)) continue;

        res.push(it);
        set.add(it);
    }
    return res;
}

export function insertionSort(arr: Array<number>) {
    for (let i = 1; i < arr.length; i++) {

        // store the current value because it may shift later
        let value = arr[i];
        let j = i - 1;

        // Whenever the value in the sorted section is greater than the value
        // in the unsorted section, shift all items in the sorted section over
        // by one. This creates space in which to insert the value.
        while (j >= 0 && arr[j] > value) {
            arr[j + 1] = arr[j];
            j--;
        }

        arr[j + 1] = value;
    }
}

export function insertionSortBy<T>(arr: Array<T>, cmp: (a: T, b: T) => number) {
    for (let i = 1; i < arr.length; i++) {

        // store the current value because it may shift later
        let value = arr[i];
        let j = i - 1;

        // Whenever the value in the sorted section is greater than the value
        // in the unsorted section, shift all items in the sorted section over
        // by one. This creates space in which to insert the value.
        while (j >= 0 && cmp(arr[j], value) > 0) {
            arr[j + 1] = arr[j];
            j--;
        }

        arr[j + 1] = value;
    }
}

export function groupBy<T>(array: T[], cmp: keyof T | ((t: T) => string)): T[][] {
    let grouped = new Map<string, T[]>();
    for (const it of array) {
        if (typeof cmp === "string") {
            getMapOrSetArray(grouped, it[cmp]).push(it);
        }
        else {
            getMapOrSetArray(grouped, (cmp as any)(it)).push(it);
        }
    }
    let res: T[][] = [];
    grouped.forEach((v, k) => {
        res.push(v);
    });
    return res;
}

export function shuffle<T>(array: T[], inPlace = false): T[] {
    let res: T[] = [];
    let copy = array.slice();
    let numIterations = copy.length;
    for (let i = 0; i < numIterations; i++) {
        let index = rngIntMax(copy.length - 1);
        res.push(copy[index]);
        fastSplice(copy, index, 1);
    }

    if (inPlace) {
        array.sort((a, b) => res.indexOf(a) - res.indexOf(b));
        return array;
    }
    else {
        return res;
    }
}

export function remove<T>(array: T[], element: T, inPlace = false): T[] {
    let copy = (inPlace) ? array : array.slice();
    const i = array.indexOf(element);
    if (i !== -1) fastSplice(array, i, 1);
    return copy;
}

export function removeIndex<T>(array: T[] | ReadonlyArray<T>, i: number, inPlace = false): T[] {
    let copy = (inPlace) ? array as T[] : array.slice();
    fastSplice(copy, i, 1);
    return copy;
}
